/**
 * Отдельный vuex модуль для взаимодействия по analytics/aircraft-API
 */

import {ANALYTICS, AnalyticSettings} from "@/store/analytics/helpers.js";
import makeMessage from "@/store/analytics/analyticMessage.js";

// actions для aircraft. Вызов действия начинать с "analytics/aircraft/"
export const ACTION_LOAD_AIRCRAFTS_SETTINGS = "LOAD_AIRCRAFTS_SETTINGS";
export const ACTION_LOAD_AIRCRAFT_SETTINGS = "LOAD_AIRCRAFT_SETTINGS";
export const ACTION_LOAD_AIRCRAFT_EVENTS = "LOAD_AIRCRAFT_EVENTS";
export const ACTION_LOAD_AIRCRAFT_EVENT = "LOAD_AIRCRAFT_EVENT";
export const ACTION_LOAD_AIRCRAFT_REPORT = "LOAD_AIRCRAFT_REPORT";
export const ACTION_LOAD_AIRCRAFT_REPORT_INFO = "LOAD_AIRCRAFT_REPORT_INFO";

/**
 * Класс для представления настроек аналитики ЛА.
 */
export class AircraftSettings extends AnalyticSettings {
  get analyticType() {
    return ANALYTICS.aircraft;
  }
}

export default {
  namespaced: true,
  state: {},
  mutations: {},
  actions: {
    /**
     * Загрузка списка настроек аналитик ЛА.
     *
     * @param {Object} context
     * @param {Array.<String>} camerasNumbers
     * @return {Promise.<Array.<AircraftSettings>>}
     */
    async [ACTION_LOAD_AIRCRAFTS_SETTINGS](context, {camerasNumbers}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/aircraft/settings/", {camera_numbers: camerasNumbers});
        return response.data.results.map(rawAircraftSettings => new AircraftSettings(rawAircraftSettings));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка настроек аналитики ЛА с конкретной камеры.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @return {Promise.<AircraftSettings>}
     */
    async [ACTION_LOAD_AIRCRAFT_SETTINGS]({dispatch}, {cameraNumber}) {
      const aircraftSettings = await dispatch(ACTION_LOAD_AIRCRAFTS_SETTINGS, {camerasNumbers: [cameraNumber]});
      if (_.isEmpty(aircraftSettings)) {
        return null;
      }
      return aircraftSettings[0];
    },
    /**
     * Загрузка перечня событий по аналитике ЛА с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Array.<Number>} eventIds
     * @return {Promise.<Array.<AircraftMessage>>}
     */
    async [ACTION_LOAD_AIRCRAFT_EVENTS](context, {cameraNumber, eventIds}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/aircraft/events/", {
          camera_number: cameraNumber,
          event_ids: eventIds,
        });
        return response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.aircraft));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка события по аналитике ЛА с камеры для формирования отчета.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @param {Number} eventId
     * @return {Promise.<AircraftMessage>}
     */
    async [ACTION_LOAD_AIRCRAFT_EVENT]({dispatch}, {cameraNumber, eventId}) {
      const aircraftEvents = await dispatch(ACTION_LOAD_AIRCRAFT_EVENTS, {cameraNumber, eventIds: [eventId]});
      if (_.isEmpty(aircraftEvents)) {
        return null;
      }
      return aircraftEvents[0];
    },
    /**
     * Загрузка перечня событий ЛА с выбранных камер или из папки для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber Номер одной камеры для существующих экранов отчета.
     * @param {Array.<String>} cameraNumbers
     * @param {Number} folderId
     * @param {String} sourceType video или audio; без фильтра загружаются оба типа.
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @param {String} query
     * @return {Promise.<Object>}
     */
    async [ACTION_LOAD_AIRCRAFT_REPORT](context, {
      cameraNumber,
      cameraNumbers,
      folderId,
      sourceType,
      archiveFrom,
      archiveTo,
      query = null,
      page,
      page_size,
    }) {
      const cameras = cameraNumbers ?? (cameraNumber ? [cameraNumber] : []);
      if (folderId == null && !cameras.length) {
        return {messages: [], response: null};
      }
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/aircraft/report/", {
          ...(folderId == null ? {camera_numbers: cameras} : {folder_id: folderId}),
          ...(sourceType ? {source_type: sourceType} : {}),
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          query: query,
          page: page,
          page_size: page_size
        });
        const messages = response.data.results.map(rawEventData => makeMessage(
          rawEventData,
          rawEventData.camera_number || (cameras.length === 1 ? cameras[0] : null),
          ANALYTICS.aircraft,
        ));

        return {
          messages,
          response: response.data
        };
      } catch (error) {
        return {
          messages: [],
          response: null,
          error,
        };
      }
    },
    /** Вспомогательные данные отчета по событиям ЛА. */
    async [ACTION_LOAD_AIRCRAFT_REPORT_INFO]() {
      const response = await this.getters.ajaxClient.post(
        "/v0/analytics/aircraft/report/info/",
        {},
      );
      return response.data;
    },
  },
};
